import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, X } from "lucide-react";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Link } from "react-router-dom";

export const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("odesie_cookie_consent");
    if (!consent) {
      // Show after a short delay
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem("odesie_cookie_consent", JSON.stringify({
      essential: true, analytics: true, marketing: true,
      timestamp: new Date().toISOString(),
    }));
    setVisible(false);
  };

  const handleAcceptEssential = () => {
    localStorage.setItem("odesie_cookie_consent", JSON.stringify({
      essential: true, analytics: false, marketing: false,
      timestamp: new Date().toISOString(),
    }));
    setVisible(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem("odesie_cookie_consent", JSON.stringify({
      essential: true, analytics, marketing,
      timestamp: new Date().toISOString(),
    }));
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="fixed bottom-0 left-0 right-0 z-[100] p-4"
          data-testid="cookie-consent-banner"
        >
          <div className="max-w-4xl mx-auto bg-slate-900 rounded-2xl shadow-2xl border border-slate-700/50 overflow-hidden">
            <div className="p-5">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-[#FF7A59] mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-white font-bold text-sm">We value your privacy</h3>
                  <p className="text-slate-400 text-xs mt-1 leading-relaxed">
                    We use essential cookies for authentication and security. Optional cookies help us improve your experience.
                    Read our{" "}
                    <Link to="/privacy" className="text-[#FF7A59] hover:underline">Privacy Policy</Link>.
                  </p>

                  <AnimatePresence>
                    {showSettings && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="mt-3 space-y-2 overflow-hidden"
                      >
                        <div className="flex items-center justify-between bg-slate-800 rounded-lg p-3">
                          <div>
                            <p className="text-white text-xs font-semibold">Essential Cookies</p>
                            <p className="text-slate-500 text-[10px]">Required for authentication and security</p>
                          </div>
                          <Switch checked disabled className="opacity-50" />
                        </div>
                        <div className="flex items-center justify-between bg-slate-800 rounded-lg p-3">
                          <div>
                            <p className="text-white text-xs font-semibold">Analytics Cookies</p>
                            <p className="text-slate-500 text-[10px]">Help us understand platform usage</p>
                          </div>
                          <Switch checked={analytics} onCheckedChange={setAnalytics} data-testid="analytics-cookie-toggle" />
                        </div>
                        <div className="flex items-center justify-between bg-slate-800 rounded-lg p-3">
                          <div>
                            <p className="text-white text-xs font-semibold">Marketing Cookies</p>
                            <p className="text-slate-500 text-[10px]">Personalized communications</p>
                          </div>
                          <Switch checked={marketing} onCheckedChange={setMarketing} data-testid="marketing-cookie-toggle" />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <Button size="sm" className="bg-[#FF7A59] hover:bg-[#FF8A6C] text-white text-xs h-8 px-4"
                      onClick={handleAcceptAll} data-testid="accept-all-cookies">
                      Accept All
                    </Button>
                    <Button size="sm" variant="outline" className="text-xs h-8 px-4 border-blue-400/50 text-blue-300 hover:bg-blue-500/20 hover:border-blue-400"
                      onClick={handleAcceptEssential} data-testid="essential-only-cookies">
                      Essential Only
                    </Button>
                    {!showSettings ? (
                      <Button size="sm" variant="ghost" className="text-xs h-8 px-4 text-slate-400 hover:text-white"
                        onClick={() => setShowSettings(true)} data-testid="cookie-settings-btn">
                        Customize
                      </Button>
                    ) : (
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 px-4"
                        onClick={handleSavePreferences} data-testid="save-cookie-prefs">
                        Save Preferences
                      </Button>
                    )}
                  </div>
                </div>
                <button onClick={handleAcceptEssential} className="text-slate-500 hover:text-white transition-colors" data-testid="close-cookie-banner">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
